import "./report.page.css";
import { Collapse, DatePicker, Drawer, Dropdown, Input, message, Popconfirm, Select, Spin, Table, Tabs, Tag, Tooltip } from "antd";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Navbar } from "@src/components/navbar";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import {
    getAllSitesForCustomerQuery,
    getCustomerPortalActivityLogQuery,
    getCustomerPortalScheduleGridQuery,
} from "@src/util/query/site.query";
import { getPayLevelQuery } from "@src/util/query/paylevel.query";
import { cancelDutyRequestMutation } from "@src/util/mutation/dutyRequest.mutation";
import { DutyRequestFormModal } from "@src/components/dutyRequestForm/dutyRequestFormModal";
import dayjs from "dayjs";
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isoWeek from 'dayjs/plugin/isoWeek';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCaretLeft, faCaretRight, faClose, faDownload, faEllipsisV, faExternalLinkAlt, faFilter, faPlus, faSearch } from "@fortawesome/free-solid-svg-icons";
import { downloadCsv, getLatLngPair, googleMapsUrl } from "@src/util/portal/download";
import { useDutyReports } from "@src/util/portal/useDutyReports";
import { LoadingOutlined, MoreOutlined } from '@ant-design/icons';

dayjs.extend(customParseFormat);
dayjs.extend(isoWeek);

const { RangePicker } = DatePicker;

const formatStatus = (status) => {
    if (!status) return "—";
    return String(status).charAt(0).toUpperCase() + String(status).slice(1);
};

// Customer-facing duty status is intentionally collapsed to just two states -
// the admin-only granular vocabulary (Open/Confirmed/Unconfirm/UnAllocated/
// On Duty/Cancelled) isn't meaningful to a customer, who only cares whether a
// duty is done or not. Mirrors schedule.service.ts's own copy of this
// (getGuardingStatusLabel/getCustomerPortalScheduleGrid's mobile statusLabel).
const getGuardingStatusLabel = (row) => (row?.isCompleted ? "Completed" : "On Schedule");
const getMobileStatusLabel = (row) => (String(row?.status || '').toLowerCase() === 'completed' ? "Completed" : "On Schedule");

// per-status colors for the Schedules tab's collapsed vocabulary, plus the
// pending/rejected duty-request pseudo-statuses merged into the same column
// (see scheduleRows' 'dutyRequest' branch) - request colors match the
// dedicated Duty Requests page's own status badges.
const GUARDING_STATUS_COLORS = {
    'On Schedule': '#00bda5',
    'Completed': '#506e91',
    'Pending': '#FFBF00',
    'Rejected': '#f75f6d',
};

// Status filter is now resolved server-side (see getCustomerPortalScheduleGrid /
// getCustomerPortalActivityLog), so its option list can no longer be derived from
// "whatever's on the current page" the way it could when everything was fetched
// client-side - hardcoded to the known, complete vocabularies instead.
const GUARDING_STATUS_OPTIONS = ['On Schedule', 'Completed'];
// 'Cancel' deliberately excluded - a cancelled mobile job never belongs in a log
// of what happened (mirrors getCustomerPortalActivityLog's own exclusion), so
// there's nothing for that filter value to ever match.
const ACTIVITY_STATUS_OPTIONS = ['Late', 'On time', 'Completed', 'Onschedule'];
// Same idea as the status vocabularies above - the full set of
// ScheduleCheckCall.callType values plus mobile service's own two synthetic
// ones (see getCustomerPortalActivityLog).
const ACTIVITY_CALL_TYPE_OPTIONS = ['Clock In', 'Clock Out', 'Check call', 'Checkpoint', 'Task', 'Report', 'Mobile Service'];

const getStatusTagColor = (status, isLate) => {
    if (GUARDING_STATUS_COLORS[status]) return GUARDING_STATUS_COLORS[status];
    if (isLate) return '#f85884';
    const normalized = String(status || '').toLowerCase();
    if (['late', 'cancel', 'reject', 'no show', 'missed'].some((k) => normalized.includes(k))) return '#f85884';
    if (['complet', 'confirm', 'on time', 'on duty', 'active', 'done'].some((k) => normalized.includes(k))) return '#0091ae';
    if (['open', 'unconfirm', 'unallocat', 'pending'].some((k) => normalized.includes(k))) return '#fb8c5f';
    return '#7c98b6';
};

const getAssigneeName = (row) => {
    const employee = row?.assignedEmployeeDetail;
    if (employee?.firstname || employee?.lastname) {
        return `${employee?.firstname || ""} ${employee?.lastname || ""}`.trim();
    }
    const customerDetail = JSON.parse(localStorage.getItem("customerDetail"));
    if (row?.assignedAgency || row?.supplierId) return customerDetail?.tenantDetail?.legalname;
    return customerDetail?.tenantDetail?.legalname;
};

// A site's own location is a different, Google-Maps-viewport-style shape
// ({northeast: {lat, lng}, southwest: {lat, lng}} - a degenerate bounding box
// for a single point, northeast === southwest) rather than actualLocation's
// plain {lat, lng}-ish shape. Used as the fallback when a specific row has no
// location of its own recorded - the site's coordinates are still more
// useful to show than a blank dash.
const getSiteLatLngPair = (siteLocation) =>
    getLatLngPair(siteLocation?.northeast) || getLatLngPair(siteLocation?.southwest);

export const Report = () => {

    // site selection lives in the route (/customer/reports/:siteId) rather than
    // component state, so a refresh (or a shared/bookmarked link) lands back on
    // the same site instead of dropping to the "no site selected" placeholder
    const { siteId } = useParams();
    const navigate = useNavigate();
    const activeSite = siteId || "";
    const setActiveSite = (id) => navigate(`/customer/reports/${id}`);

    const [customerSites, setCustomerSites] = useState([]);
    const [customerDetail, setCustomerDetail] = useState(null);

    const [activeTab, setActiveTab] = useState("schedules");
    const [dateRange, setDateRange] = useState([dayjs().startOf('isoWeek'), dayjs().endOf('isoWeek')]);
    const [dutyTypeFilter, setDutyTypeFilter] = useState(null);
    const [statusFilter, setStatusFilter] = useState(null);
    // Activity Logs tab only - filters the same callType a row's Action Type
    // column shows, guarding vocabulary (Clock In/Clock Out/Check call/
    // Checkpoint/Task/Report) and mobile service's own (Job Started/Job
    // Finished) alike.
    const [callTypeFilter, setCallTypeFilter] = useState(null);

    // debounced so typing doesn't fire a backend request per keystroke - this is a
    // real server-side search now, not a client-side filter over already-fetched rows
    const [assigneeSearchInput, setAssigneeSearchInput] = useState("");
    const [assigneeSearch, setAssigneeSearch] = useState("");
    useEffect(() => {
        const timeout = setTimeout(() => setAssigneeSearch(assigneeSearchInput.trim()), 400);
        return () => clearTimeout(timeout);
    }, [assigneeSearchInput]);

    // Duty Type / Status live in a Drawer (see .portal-filter-drawer below) as
    // ordinary searchable Selects - closed by default like any other Select,
    // not an always-open list. Assignee stays a plain search field in the main
    // filters row (it's a live server-side search, not a pick-from-list).
    const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
    const activeFilterCount = [dutyTypeFilter, statusFilter, callTypeFilter].filter(Boolean).length;
    const clearAllFilters = () => {
        setDutyTypeFilter(null);
        setStatusFilter(null);
        setCallTypeFilter(null);
        setAssigneeSearchInput("");
    };

    // date filter is a free-form range (RangePicker below), not locked to a
    // calendar week - prev/next arrows step by the span of whatever range is
    // currently selected (a 3-day range steps by 3 days, a month range by
    // ~30) so paging still makes sense once the range is no longer a fixed week.
    const shiftDateRange = (direction) => {
        setDateRange((prev) => {
            const start = dayjs(prev?.[0] || dayjs());
            const end = dayjs(prev?.[1] || start);
            const spanDays = end.diff(start, 'day') + 1;
            const offsetDays = spanDays * direction;
            return [start.add(offsetDays, 'day'), end.add(offsetDays, 'day')];
        });
    };

    // server-driven pagination - every page/pageSize change is a fresh backend
    // request, never a client-side slice of an already-fetched blob
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);

    // Date/Time column sort - also resolved on the backend (see sortMultiplier in
    // getCustomerPortalScheduleGrid/getCustomerPortalActivityLog), never a
    // client-side re-sort of the one page of rows already on hand. Descending by default.
    const [sortOrder, setSortOrder] = useState('desc');

    // any filter change invalidates the current page
    useEffect(() => { setPage(1); }, [activeSite, activeTab, dutyTypeFilter, statusFilter, callTypeFilter, assigneeSearch, dateRange]);

    const { isBusy, downloadReport, downloadScheduleTaskReport, downloadDetailDutyReport, downloadDutyDetailPdf } = useDutyReports();

    // Add/Edit Duty Request modal (Schedules tab only) - editingRequest holds the
    // raw request row being edited, null while creating a new one.
    const [requestModalOpen, setRequestModalOpen] = useState(false);
    const [editingRequest, setEditingRequest] = useState(null);
    const [deletingRequestId, setDeletingRequestId] = useState(null);
    const [cancelDutyRequest] = useMutation(cancelDutyRequestMutation);

    const storedCustomerDetail = localStorage.getItem('customerDetail');
    useEffect(() => {
        if (storedCustomerDetail) {
            setCustomerDetail(JSON.parse(storedCustomerDetail));
        }
    }, [storedCustomerDetail]);

    const { data: sitesForCustomer, loading: sitesLoading } = useQuery(getAllSitesForCustomerQuery, {
        variables: { customerId: customerDetail?._id },
        skip: !customerDetail?._id,
        fetchPolicy: 'network-only'
    });

    useEffect(() => {
        if (!sitesLoading) {
            setCustomerSites(sitesForCustomer?.getAllSiteForCustomer?.response);
        }
    }, [sitesForCustomer?.getAllSiteForCustomer?.response, sitesLoading]);

    const handelSearch = (keyword) => {
        if (keyword?.length > 0) {
            setCustomerSites(sitesForCustomer?.getAllSiteForCustomer?.response?.filter((site) => site?.sitename?.toLowerCase()?.includes(keyword?.toLowerCase())));
        } else {
            setCustomerSites(sitesForCustomer?.getAllSiteForCustomer?.response);
        }
    };

    const { data: dutyTypeData } = useQuery(getPayLevelQuery);
    const dutyTypes = dutyTypeData?.getPayLevel?.response || [];

    const startDate = dateRange?.[0]?.format('YYYY-MM-DD');
    const endDate = dateRange?.[1]?.format('YYYY-MM-DD');

    // header stat strip (Contracted/Scheduled/Diff) is shared by both tabs, so this
    // is fetched whenever a site+range is selected, independent of the active tab.
    // Also drives the Schedules tab's paginated grid - filters + page/pageSize are
    // all applied backend-side (see getCustomerPortalScheduleGrid), never sliced
    // client-side from an already-fetched blob.
    const { data: scheduleGridData, loading: scheduleGridLoading, refetch: refetchScheduleGrid } = useQuery(getCustomerPortalScheduleGridQuery, {
        variables: {
            siteId: activeSite, customerId: customerDetail?._id, startDate, endDate,
            search: assigneeSearch || undefined,
            dutyTypeId: dutyTypeFilter || undefined,
            status: activeTab === 'schedules' ? (statusFilter || undefined) : undefined,
            // pinned to page 1 while this tab isn't the active one, so paginating the
            // Activity Logs tab doesn't also re-trigger this (stats-only, in that case)
            // query on every page turn
            page: activeTab === 'schedules' ? page : 1,
            pageSize: activeTab === 'schedules' ? pageSize : 20,
            sortOrder,
        },
        skip: !activeSite || !customerDetail?._id || !startDate || !endDate,
        fetchPolicy: 'network-only',
        errorPolicy: 'all'
    });

    const { data: activityLogData, loading: activityLogLoading } = useQuery(getCustomerPortalActivityLogQuery, {
        variables: {
            siteId: activeSite, customerId: customerDetail?._id, startDate, endDate,
            search: assigneeSearch || undefined,
            status: activeTab === 'activity' ? (statusFilter || undefined) : undefined,
            callType: activeTab === 'activity' ? (callTypeFilter || undefined) : undefined,
            page: activeTab === 'activity' ? page : 1,
            pageSize: activeTab === 'activity' ? pageSize : 20,
            sortOrder,
        },
        skip: !activeSite || !customerDetail?._id || !startDate || !endDate || activeTab !== "activity",
        fetchPolicy: 'network-only',
        errorPolicy: 'all'
    });

    const gridResponse = scheduleGridData?.getCustomerPortalScheduleGrid?.response;
    const contractedHours = gridResponse?.contractedHours ?? 0;
    const scheduledHours = gridResponse?.scheduledHours ?? 0;
    // diffHours (= contracted - scheduled) is negative once scheduled hours run
    // past the contracted target - shown as "Overtime" (the positive amount)
    // rather than a negative Diff figure.
    const diffHours = gridResponse?.diffHours ?? 0;
    const isOvertime = diffHours < 0;
    const diffLabel = isOvertime ? 'Overtime' : 'Diff';
    const diffValue = isOvertime ? Math.abs(diffHours) : diffHours;
    const scheduleTotalCount = gridResponse?.totalCount ?? 0;

    // normalize this page of schedule + mobile service duties + pending/rejected
    // duty requests into one uniform row shape
    const scheduleRows = useMemo(() => (
        (gridResponse?.duties || []).map((row) => {
            if (row.source === 'dutyRequest') {
                // A request's "time" fields are whatever the customer typed on the
                // form, not a real duty's actual schedule - preferredStartTime for a
                // guarding request carries the right clock time but today's date (see
                // dutyRequestFormModal.jsx's handleSave), so only its time portion is
                // trustworthy there.
                const startPart = row?.preferredStartTime?.split(' ') || [];
                const endPart = row?.preferredEndTime?.split(' ') || [];
                return {
                    key: row?._id,
                    source: 'dutyRequest',
                    rawRequest: row,
                    requestStatus: row?.requestStatus,
                    date: row?.preferredDate
                        || (startPart[0] && row?.serviceType === 'mobile' ? startPart[0] : null)
                        || (row?.scheduleType === 'ufn' ? 'Until Further Notice' : '—'),
                    assignee: "—",
                    shiftTime: (startPart[1] || startPart[0]) && (endPart[1] || endPart[0])
                        ? `${startPart[1] || startPart[0]} - ${endPart[1] || endPart[0]}`
                        : "—",
                    dutyType: row?.dutyTypeDetail?.name || "—",
                    status: row?.requestStatus === 'pending' ? 'Pending' : 'Rejected',
                    hours: "—",
                    jobId: row?._id,
                    templateId: null,
                    reportAvailable: false,
                };
            }
            if (row.source === 'mobileService') {
                return {
                    key: row?._id,
                    source: 'mobileService',
                    date: dayjs(row?.startTime).format('DD/MM/YYYY'),
                    assignee: getAssigneeName(row),
                    shiftTime: `${dayjs(row?.startTime).format('HH:mm')} - ${dayjs(row?.endTime).format('HH:mm')}`,
                    dutyType: row?.dutyTypeDetail?.name || "—",
                    status: getMobileStatusLabel(row),
                    hours: (row?.startTime && row?.endTime)
                        ? dayjs(row.endTime).diff(dayjs(row.startTime), 'hour', true).toFixed(2)
                        : "0.00",
                    jobId: row?._id,
                    templateId: row?.scheduleReport?.templateId,
                    reportAvailable: !!row?.scheduleReport?.templateId,
                };
            }
            return {
                key: row?._id,
                source: 'schedule',
                date: row?.date || dayjs(row?.firstDate).format('DD/MM/YYYY'),
                assignee: getAssigneeName(row),
                shiftTime: `${row?.shiftStartTime || "—"} - ${row?.shiftEndTime || "—"}`,
                dutyType: row?.dutyTypeDetail?.name || "—",
                status: getGuardingStatusLabel(row),
                hours: Number((parseFloat(row?.firstDateHours) || 0) + (parseFloat(row?.secondDateHours) || 0)).toFixed(2),
                jobId: row?._id,
                templateId: row?.scheduleReport?.templateId,
                reportAvailable: !!row?.scheduleReport?.templateId,
            };
        })
    ), [gridResponse]);

    const activityResponse = activityLogData?.getCustomerPortalActivityLog?.response;
    const activityTotalCount = activityResponse?.totalCount ?? 0;

    // Fallback shown when a specific row has no location of its own recorded.
    const activeSiteLatLng = useMemo(
        () => getSiteLatLngPair(customerSites?.find((site) => site?._id === activeSite)?.location),
        [customerSites, activeSite]
    );

    // normalize this page of activity rows into one uniform shape
    const activityRows = useMemo(() => (
        (activityResponse?.activity || []).map((row) => ({
            key: row?._id,
            // Expected/requiredAt vs Actual - for mobile-service rows these are the
            // same instant (see getCustomerPortalActivityLog: a job has no
            // separately-scheduled "expected" time per Started/Finished event).
            // Actual is one merged date+time column, not split across two.
            expectedDate: row?.requiredAt ? dayjs(row.requiredAt).format('DD/MM/YYYY HH:mm') : "—",
            actualDateTime: dayjs(row?.actualDate).format('DD/MM/YYYY HH:mm'),
            assignee: getAssigneeName(row),
            dutyType: row?.dutyTypeDetail?.name || "—",
            actionType: row?.label && row?.label !== row?.callType ? `${row?.callType} - ${row?.label}` : row?.callType,
            latLngPair: getLatLngPair(row?.actualLocation) || activeSiteLatLng,
            // Mobile service rows always read as "On time" here - there's no
            // late/on-time concept tracked for them the way a checkcall has one.
            status: row?.source !== 'mobileService' && row?.late ? "Late" : "On time",
            late: row?.source !== 'mobileService' && !!row?.late,
            jobId: row?.activityReport?.jobId,
            templateId: row?.activityReport?.templateId,
            imageLink: row?.checkCallActivityImage,
            // Task check calls (scheduleAssignedTaskId) with a completed custom-field
            // submission but no schedulereport/templateId - checkCallId is what
            // downloadScheduleTaskReport hits wfc-server with; row._id IS the
            // checkcall's own id for 'schedule'-sourced rows (mobileService rows use a
            // synthetic id and never carry scheduleTaskReport, see getCustomerPortalActivityLog)
            scheduleTaskReportAvailable: !!row?.scheduleTaskReport,
            checkCallId: row?.source !== 'mobileService' ? row?._id : undefined,
            reportAvailable: !!row?.activityReport?.templateId || !!row?.checkCallActivityImage || !!row?.scheduleTaskReport,
        }))
    ), [activityResponse, activeSiteLatLng]);

    const handleDeleteRequest = async (requestId) => {
        setDeletingRequestId(requestId);
        try {
            await cancelDutyRequest({ variables: { requestId } });
            message.success("Duty request deleted successfully");
            refetchScheduleGrid();
        } catch (err) {
            message.error(err?.message || "Failed to delete duty request.");
        } finally {
            setDeletingRequestId(null);
        }
    };

    const scheduleColumns = [
        {
            title: 'Date',
            dataIndex: 'date',
            key: 'date',
            sorter: true,
            sortOrder: sortOrder === 'asc' ? 'ascend' : 'descend',
        },
        { title: 'Assignee', dataIndex: 'assignee' },
        { title: 'Shift Time', dataIndex: 'shiftTime' },
        { title: 'Duty Type', dataIndex: 'dutyType' },
        {
            title: 'Status',
            dataIndex: 'status',
            render: (status) => (
                <Tag className="portal-tag" color={getStatusTagColor(status)}>
                    {formatStatus(status)}
                </Tag>
            )
        },
        { title: 'Hour', dataIndex: 'hours' },
        {
            title: '',
            key: 'activity',
            width: 56,
            align: 'center',
            render: (_, record) => {
                const isDownloading = record.source === 'dutyRequest'
                    ? deletingRequestId === record.jobId
                    : isBusy(record.jobId);

                const menuItems = record.source === 'dutyRequest'
                    ? [
                        // Only a still-pending request can be edited (matches
                        // updateDutyRequest's own server-side check) - a rejected one
                        // can only be deleted, not amended and resubmitted in place.
                        ...(record.requestStatus === 'pending' ? [{
                            key: 'edit-request',
                            label: 'Edit Request',
                            onClick: () => { setEditingRequest(record.rawRequest); setRequestModalOpen(true); },
                        }] : []),
                        {
                            key: 'delete-request',
                            label: (
                                <Popconfirm
                                    title="Delete this request?"
                                    description="This action cannot be undone."
                                    okText="Yes, delete"
                                    cancelText="No"
                                    okButtonProps={{ danger: true }}
                                    onConfirm={() => handleDeleteRequest(record.jobId)}
                                >
                                    <span onClick={(e) => e.stopPropagation()}>Delete Request</span>
                                </Popconfirm>
                            ),
                        },
                    ]
                    : record.source === 'mobileService'
                    ? [{
                        key: 'download-form',
                        label: record.reportAvailable ? 'Download Duty Report' : 'No report submitted yet',
                        disabled: !record.reportAvailable,
                        onClick: () => downloadReport(record.jobId, record.templateId),
                    }]
                    : [
                        {
                            key: 'download-detail',
                            label: 'Download Detail Duty Report (CSV)',
                            onClick: () => downloadDetailDutyReport(record.jobId, activeSite),
                        },
                        {
                            // The full PDF stitches together the duty's whole activity
                            // timeline plus every checkpoint/task's submitted report - not
                            // meaningful (and possibly still missing entries) until the
                            // duty itself is marked Completed. The menu still opens either
                            // way; only this option is greyed out until then.
                            key: 'download-duty-pdf',
                            label: record.status === 'Completed'
                                ? 'Download Full Duty Report (PDF)'
                                : 'Full Duty Report (available once completed)',
                            disabled: record.status !== 'Completed',
                            onClick: () => downloadDutyDetailPdf(record.jobId),
                        },
                    ];

                return (
                    <Dropdown trigger={['click']} menu={{ items: menuItems }} placement="bottomRight">
                        <button className="row-menu-btn" disabled={isDownloading}>
                            {isDownloading
                                ? <Spin indicator={<LoadingOutlined style={{ fontSize: 15, color: '#0091ae' }} spin />} />
                                : <FontAwesomeIcon icon={faEllipsisV}/>}
                        </button>
                    </Dropdown>
                );
            }
        },
    ];

    // "Export" (Activity Logs tab, right side of the toolbar) - the log rows
    // themselves as one CSV, not the per-row PDF reports the Download column
    // already handles. The on-screen table is capped to a page at a time
    // (max 100/page, see clampPagination server-side) - this loops through
    // every page for the current filters+date range instead, up to a generous
    // safety ceiling so a huge range can't runaway the browser.
    const [downloadingActivityLog, setDownloadingActivityLog] = useState(false);
    const [fetchActivityLogPage] = useLazyQuery(getCustomerPortalActivityLogQuery, { fetchPolicy: 'network-only' });
    const EXPORT_PAGE_SIZE = 100;
    const EXPORT_MAX_ROWS = 5000;

    const downloadActivityLogCsv = async () => {
        if (!activeSite || !customerDetail?._id || !startDate || !endDate) return;

        setDownloadingActivityLog(true);
        try {
            let allRows = [];
            let page = 1;
            let totalCount = Infinity;

            while (allRows.length < totalCount && allRows.length < EXPORT_MAX_ROWS) {
                const { data } = await fetchActivityLogPage({
                    variables: {
                        siteId: activeSite, customerId: customerDetail._id, startDate, endDate,
                        search: assigneeSearch || undefined,
                        status: statusFilter || undefined,
                        callType: callTypeFilter || undefined,
                        page, pageSize: EXPORT_PAGE_SIZE, sortOrder,
                    },
                });
                const response = data?.getCustomerPortalActivityLog?.response;
                const pageRows = response?.activity || [];
                totalCount = response?.totalCount ?? pageRows.length;
                if (!pageRows.length) break;
                allRows = allRows.concat(pageRows);
                page += 1;
            }

            if (!allRows.length) {
                message.info("No activity found for this date range.");
                return;
            }

            const truncated = totalCount > allRows.length;

            const csvRows = allRows.map((row) => {
                const latLng = getLatLngPair(row?.actualLocation) || activeSiteLatLng;
                return [
                    row?.label && row?.label !== row?.callType ? `${row.callType} - ${row.label}` : row?.callType,
                    row?.dutyTypeDetail?.name || "",
                    getAssigneeName(row),
                    row?.requiredAt ? dayjs(row.requiredAt).format('DD/MM/YYYY HH:mm') : "",
                    row?.actualDate ? dayjs(row.actualDate).format('DD/MM/YYYY HH:mm') : "",
                    latLng ? latLng.lat : "",
                    latLng ? latLng.lng : "",
                    row?.source !== 'mobileService' && row?.late ? "Late" : "On time",
                ];
            });

            downloadCsv(
                `activity-log-${activeSite}-${startDate}-to-${endDate}.csv`,
                ["Action Type", "Duty Type", "Assignee", "Expected", "Actual", "Lat", "Lng", "Status"],
                csvRows
            );
            message.success(
                truncated
                    ? `Activity log downloaded (first ${allRows.length} of ${totalCount} rows - narrow the date range for the rest)`
                    : "Activity log downloaded"
            );
        } catch (err) {
            message.error("Failed to download activity log.");
        } finally {
            setDownloadingActivityLog(false);
        }
    };

    const activityColumns = [
        { title: 'Action Type', dataIndex: 'actionType' },
        { title: 'Duty Type', dataIndex: 'dutyType' },
        { title: 'Assignee', dataIndex: 'assignee' },
        { title: 'Expected', dataIndex: 'expectedDate' },
        {
            title: 'Actual',
            dataIndex: 'actualDateTime',
            key: 'actualDateTime',
            sorter: true,
            sortOrder: sortOrder === 'asc' ? 'ascend' : 'descend',
        },
        {
            title: 'Lat, Lng',
            key: 'latLng',
            render: (_, record) => record.latLngPair
                ? <span className="lat-lng-cell">
                    {record.latLngPair.lat.toFixed(5)}, {record.latLngPair.lng.toFixed(5)}
                    <a
                        href={googleMapsUrl(record.latLngPair)}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Open in Google Maps"
                        className="lat-lng-map-link"
                    >
                        <FontAwesomeIcon icon={faExternalLinkAlt} />
                    </a>
                  </span>
                : "—"
        },
        {
            title: 'Status',
            dataIndex: 'status',
            render: (status, record) => <Tag className="portal-tag" color={getStatusTagColor(status, record.late)}>{status}</Tag>
        },
        {
            title: 'Download',
            key: 'download',
            render: (_, record) => {
                if (record.jobId && record.templateId) {
                    return (
                        <button className="btn" onClick={() => downloadReport(record.jobId, record.templateId)} disabled={isBusy(record.jobId)}>
                            {isBusy(record.jobId)
                                ? <Spin indicator={<LoadingOutlined style={{ fontSize: 16, color: '#0091ae' }} spin />} />
                                : <FontAwesomeIcon icon={faDownload} />}
                        </button>
                    );
                }
                if (record.scheduleTaskReportAvailable) {
                    return (
                        <button className="btn" onClick={() => downloadScheduleTaskReport(record.checkCallId)} disabled={isBusy(record.checkCallId)}>
                            {isBusy(record.checkCallId)
                                ? <Spin indicator={<LoadingOutlined style={{ fontSize: 16, color: '#0091ae' }} spin />} />
                                : <FontAwesomeIcon icon={faDownload} />}
                        </button>
                    );
                }
                if (record.imageLink) {
                    return (
                        <button className="btn" onClick={() => window.open(record.imageLink, '_blank')}>
                            <FontAwesomeIcon icon={faDownload} />
                        </button>
                    );
                }
                return <span style={{ color: '#94a3b8', fontSize: 12 }}>—</span>;
            }
        },
    ];

    // single handler for both tables - antd reports pagination and column-sort
    // changes together, and both drive a fresh backend request here (never a
    // client-side re-page/re-sort of the one page of rows already on hand)
    const handleTableChange = (paginationInfo, _filters, sorter) => {
        const nextSortOrder = sorter?.order ? (sorter.order === 'ascend' ? 'asc' : 'desc') : sortOrder;
        const sortChanged = nextSortOrder !== sortOrder;
        if (sortChanged) setSortOrder(nextSortOrder);
        setPage(sortChanged ? 1 : paginationInfo.current);
        setPageSize(paginationInfo.pageSize);
    };

    const activeSiteName = customerSites?.find((site) => site?._id === activeSite)?.sitename;

    return (

        <div className="report-page-shell">

            <Navbar
                customerDetail={customerDetail}
            />

            <div className="report-page-row">

                <div className='setting-sidebar'>

                    {/* Title + search stay pinned to the top of the sidebar - only the
                        site list below scrolls (see .setting-sidebar-body) */}
                    <div className="setting-sidebar-inner setting-sidebar-fixed">
                        <div className="setting-heading">
                            <div className='setting-header-title'>
                                {/* count is the customer's full site list, not the (possibly
                                    search-filtered) list rendered below - so it doesn't
                                    fluctuate as the sidebar search narrows the visible rows */}
                                {`All Sites (${sitesForCustomer?.getAllSiteForCustomer?.response?.length || 0})`}
                            </div>
                        </div>

                        <Input
                            placeholder="Search ..."
                            className="generic-input-control mb16"
                            onChange={(e) => handelSearch(e?.target?.value)}
                        />
                    </div>

                    <div className='setting-sidebar-body'>
                        <div className="setting-sidebar-inner setting-sidebar-scroll-inner">
                            <nav className='setting-custom-nav' style={{ paddingBottom: '32px', display: 'flex', justifyContent: 'space-between', flexDirection: 'column' }}>
                                <Collapse defaultActiveKey={['1', '2', '3', '4']}>

                                    {
                                        customerSites?.map((site) => {
                                            return (

                                                // Row itself must stay a plain DOM element (not a component like
                                                // Tooltip) here - it's a direct child of antd's <Collapse>, which
                                                // is only safe for these non-Panel divs because rc-collapse
                                                // special-cases a string element type and renders it untouched;
                                                // any other child type gets its props hijacked by rc-collapse's
                                                // legacy panel-cloning (including a forced prefixCls, which broke
                                                // Tooltip's own styling/positioning and left a stray overlay
                                                // blocking the sidebar's scroll). Tooltip goes one level deeper,
                                                // around just the name text, so it's unaffected.
                                                <div
                                                    key={site?._id}
                                                    className={activeSite === site?._id ? "setting-sidebar-nav  activeSite" : 'setting-sidebar-nav siteList'}
                                                    onClick={() => setActiveSite(site?._id)}
                                                    // style={activeSite === site?._id ? { background: '#e5f5f8' } : {}}
                                                >
                                                    <Tooltip title={site?.sitename} placement="right">
                                                        <span>{site?.sitename}</span>
                                                    </Tooltip>
                                                </div>

                                            )
                                        })
                                    }




                                </Collapse>


                            </nav>

                        </div>
                    </div>
                </div>


                <div className={activeSite ? 'hr-section setting-body' : 'hr-section setting-body section-bg'}
                    style={{ margin: '15px' }}
                >

                    <div style={{ paddingRight: '16px' }}>
                        <div className='hr-info-title'>
                            <div>{(activeSiteName || "Site") + "  Schedule"}</div>
                        </div>
                    </div>

                    <div className="report-body-scroll">
                    {
                        activeSite ?
                            <div className="report-content-flex">
                                    <div className="portal-stats">
                                        <div className="portal-stat">
                                            <div className="portal-stat-value">{scheduleGridLoading ? <Spin size="small" /> : contractedHours}</div>
                                            <div className="portal-stat-label">Contracted Hrs.</div>
                                        </div>
                                        <div className="portal-stat">
                                            <div className="portal-stat-value">{scheduleGridLoading ? <Spin size="small" /> : scheduledHours}</div>
                                            <div className="portal-stat-label">Scheduled Hrs.</div>
                                        </div>
                                        <div className="portal-stat">
                                            <div className="portal-stat-value">{scheduleGridLoading ? <Spin size="small" /> : diffValue}</div>
                                            <div className="portal-stat-label">{diffLabel}</div>
                                        </div>
                                    </div>
                                    <div className="setting-body-inner grid-tabs">

                                        <Tabs
                                            // className="brand-tabs"
                                            activeKey={activeTab}
                                            onChange={setActiveTab}
                                            items={[
                                                { key: 'schedules', label: 'Schedules' },
                                                { key: 'activity', label: 'Activity Logs' },
                                            ]}
                                        />
                                    </div>


                                <div className="portal-filters-row">
                                    <div className="left-filter-inner" >
                                        <div className="report-date-nav">
                                                <span className="report-date-nav-btn" onClick={() => shiftDateRange(-1)}>
                                                    <FontAwesomeIcon icon={faCaretLeft} />
                                                </span>
                                                <RangePicker
                                                    className="report-date-range-picker"
                                                    value={dateRange}
                                                    format="DD MMM, YYYY"
                                                    allowClear={false}
                                                    // only commit once both ends are picked - a still-in-progress
                                                    // selection (one end null) leaves the previous range, and its
                                                    // already-fetched data, in place rather than tearing it down
                                                    onChange={(dates) => { if (dates?.[0] && dates?.[1]) setDateRange(dates); }}
                                                />
                                                <span className="report-date-nav-btn" onClick={() => shiftDateRange(1)}>
                                                    <FontAwesomeIcon icon={faCaretRight} />
                                                </span>
                                        </div>
                                        {/* <Input
                                            type="search"
                                            className="generic-input-control"
                                            placeholder="Search by assignee ..."
                                            allowClear
                                            style={{ maxWidth: 320 }}
                                            value={assigneeSearchInput}
                                            onChange={(e) => setAssigneeSearchInput(e?.target?.value)}
                                            suffix={<FontAwesomeIcon style={{ color: '#0091ae' }} icon={faSearch} />}
                                        /> */}

                                        <span className="grid-text-btn" onClick={() => setFilterDrawerOpen(true)}>
                                            <FontAwesomeIcon color="#0091ae" icon={faFilter} />
                                            &nbsp;Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
                                        </span>
                                        {activeFilterCount > 0 && (
                                            <span className="grid-text-btn" onClick={clearAllFilters}>Clear all</span>
                                        )}
                                    </div>

                                    <div className="right-filter-inner">
                                        {activeTab === 'schedules' && (
                                            <button
                                                className="drawer-filled-btn"
                                                style={{ display: "flex", alignItems: "center", gap: 8 }}
                                                onClick={() => { setEditingRequest(null); setRequestModalOpen(true); }}
                                            >
                                                <FontAwesomeIcon icon={faPlus} />
                                                Add Duty Request
                                            </button>
                                        )}
                                        {activeTab === 'activity' && (
                                            <button
                                                className="drawer-filled-btn"
                                                style={{ display: "flex", alignItems: "center", gap: 8 }}
                                                onClick={downloadActivityLogCsv}
                                                disabled={downloadingActivityLog}
                                            >
                                                {downloadingActivityLog
                                                    ? <Spin indicator={<LoadingOutlined style={{ color: "#fff" }} spin />} />
                                                    : <FontAwesomeIcon icon={faDownload} />}
                                                Export as CSV
                                            </button>
                                        )}
                                    </div>
                                </div>

                                <Drawer
                                    title="Filters"
                                    placement="right"
                                    className="portal-filter-drawer"
                                    closable={true}
                                    onClose={() => setFilterDrawerOpen(false)}
                                    closeIcon={<FontAwesomeIcon icon={faClose} className="close-icon" />}
                                    open={filterDrawerOpen}
                                    width={500}
                                    footer={
                                        <div className="drawer-footer">
                                            <button className="drawer-outlined-btn" onClick={clearAllFilters}>Clear all</button>
                                            <button className="drawer-filled-btn" onClick={() => setFilterDrawerOpen(false)}>Done</button>
                                        </div>
                                    }
                                >
                                    {activeTab === 'schedules' && (
                                        <div className="filter-section">
                                            <div>Duty type</div>
                                            <Select
                                                className="custom-select"
                                                style={{ width: '100%' }}
                                                placeholder="All duty types"
                                                allowClear
                                                showSearch
                                                optionFilterProp="label"
                                                value={dutyTypeFilter}
                                                onChange={setDutyTypeFilter}
                                                options={dutyTypes.map((dt) => ({ label: dt.name, value: dt._id }))}
                                            />
                                        </div>
                                    )}

                                    <div className="filter-section">
                                        <div>Status</div>
                                        <Select
                                            className="custom-select"
                                            style={{ width: '100%' }}
                                            placeholder="All statuses"
                                            allowClear
                                            showSearch
                                            optionFilterProp="label"
                                            value={statusFilter}
                                            onChange={setStatusFilter}
                                            options={(activeTab === 'schedules' ? GUARDING_STATUS_OPTIONS : ACTIVITY_STATUS_OPTIONS)
                                                .map((s) => ({ label: s, value: s }))}
                                        />
                                    </div>

                                    {activeTab === 'activity' && (
                                        <div className="filter-section">
                                            <div>Action type</div>
                                            <Select
                                                className="custom-select"
                                                style={{ width: '100%' }}
                                                placeholder="All action types"
                                                allowClear
                                                showSearch
                                                optionFilterProp="label"
                                                value={callTypeFilter}
                                                onChange={setCallTypeFilter}
                                                options={ACTIVITY_CALL_TYPE_OPTIONS.map((s) => ({ label: s, value: s }))}
                                            />
                                        </div>
                                    )}
                                </Drawer>

                                {/* Only this table's own rows scroll (see .portal-table-scroll-wrap /
                                    .portal-table.ant-table-wrapper in report.page.css) - the column
                                    header and the pagination footer stay fixed in place. */}
                                <div className="portal-table-scroll-wrap">
                                    {activeTab === 'schedules'
                                        ? <Table
                                            className="portal-table mt16"
                                            columns={scheduleColumns}
                                            loading={scheduleGridLoading}
                                            dataSource={scheduleRows}
                                            locale={{ emptyText: 'No schedules found for this range' }}
                                            bordered={false}
                                            // Date column is always sorted (sortOrder above is only ever
                                            // 'ascend'/'descend', never unsorted), but antd's default 2-entry
                                            // cycle (['ascend','descend']) has no "next" after 'descend' - the
                                            // click there reports sorter.order as undefined and handleTableChange
                                            // (reasonably) treats that as no change, so the column got stuck at
                                            // its default descend forever. The 3rd entry makes the cycle wrap
                                            // 'descend' -> 'ascend' instead of falling off the end.
                                            sortDirections={['ascend', 'descend', 'ascend']}
                                            onChange={handleTableChange}
                                            pagination={{
                                                current: page,
                                                pageSize,
                                                total: scheduleTotalCount,
                                                showSizeChanger: true,
                                                pageSizeOptions: ['20', '50', '100'],
                                                showTotal: (total) => `${total} duties`,
                                            }}
                                        />
                                        : <Table
                                            className="portal-table mt16"
                                            columns={activityColumns}
                                            loading={activityLogLoading}
                                            dataSource={activityRows}
                                            locale={{ emptyText: 'No activity found for this range' }}
                                            bordered={false}
                                            // see the matching comment on the Schedules table above
                                            sortDirections={['ascend', 'descend', 'ascend']}
                                            onChange={handleTableChange}
                                            pagination={{
                                                current: page,
                                                pageSize,
                                                total: activityTotalCount,
                                                showSizeChanger: true,
                                                pageSizeOptions: ['20', '50', '100'],
                                                showTotal: (total) => `${total} activities`,
                                            }}
                                        />
                                    }
                                </div>
                            </div>
                            :
                            <div className="mt16">
                                Select a site from the left to display Schedules and Activity Logs here.
                            </div>
                    }
                    </div>


                </div>

            </div>

            {requestModalOpen && (
                <DutyRequestFormModal
                    visible={requestModalOpen}
                    onClose={() => { setRequestModalOpen(false); setEditingRequest(null); }}
                    sites={customerSites}
                    lockedSiteId={activeSite}
                    editingRequest={editingRequest}
                    onSuccess={() => refetchScheduleGrid()}
                />
            )}
        </div>

    )

}
